import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { ButtonHandlerService } from '@app/core/services/button-handler.service';
import { ViewPart } from '@core/models';
import { Block } from '@core/models/block/block.model';
import { ApiInventoryService } from '@core/services/api/api-inventory.service';
import { SubSink } from '@core/utils/subsink';
import { AdvancedFiltersService } from '@shared/services/filters/advanced-filters.service';
import { ActiveFilterData } from '@shared/services/filters/types';
import { ListDetailsService } from '@shared/services/list/list-details.service';
import { Observable } from 'rxjs';

interface ActiveItem {
  uid: string;
  block: Block;
  item: ActiveFilterData;
}

@Component({
  selector: 'app-active-filter-preview',
  styleUrls: ['./active-filter-preview.component.scss'],
  template: `
    @if (items.length > 0) {
      <div class="active-filters"
           [class]="groupContainer.uId"
           [class.disabled]="listLoading$ | async"
           [attr.tabindex]="groupContainer.tabindex">

        @for (item of items; track item.uid; let idx = $index) {
          <div app-block
               [item]="item.block"
               [appClickableBlock]="item.block"
               [class]="item.block.uId"
               [attr.tabindex]="item.block.tabindex"
               (click)="onClickLabel(item.item.type, item.item.id)">
          </div>
        }

      </div>
    }
  `,
  providers: [ButtonHandlerService],
  standalone: false,
})
export class ActiveFilterPreviewComponent implements OnChanges, OnInit, OnDestroy {
  @Input() block: Block;
  groupContainer: Block;
  itemContainer: Block;
  items: ActiveItem[] = [];
  /**
   * Globalny stan ładowania listy — blokuje interakcję z chipami dopóki
   * lista nie zostanie odświeżona po poprzednim usunięciu filtra. Chipy są
   * widoczne również poza panelem zaawansowanych filtrów, więc opieramy
   * blokadę o stan, który obowiązuje niezależnie od tego, czy popup jest otwarty.
   */
  readonly listLoading$: Observable<boolean> = ListDetailsService.isLoading.asObservable();
  private subSink = new SubSink();

  constructor(
    private advanceFilterService: AdvancedFiltersService,
    private apiInventoryService: ApiInventoryService,
  ) {
  }

  ngOnInit(): void {
    const filters = this.advanceFilterService.getAvailableFiltersValue();
    if (Object.keys(filters).length === 0) {
      this.subSink.sink = this.apiInventoryService.getInventoryFilters().subscribe(filters => {
        this.advanceFilterService.initializeAvailableFilters(filters);
      });
    }
    this.subSink.sink = this.advanceFilterService.activeFiltersListUpdated$.subscribe(value => {
      const items: ActiveItem[] = [];
      value.forEach(item => {
        items.push({
          uid: `${item.type}-${item.id}`,
          item,
          block: this.makeBlockWithContent(item),
        });
      });
      this.items = items;
    });
  }

  ngOnChanges(): void {
    if (!this.block) {
      return;
    }

    if (!this.initializeGroupContainerViewPart()) {
      throw new Error('header template missing');
    }

    if (!this.initializeItemContainerViewPart()) {
      throw new Error('item template missing');
    }
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  onClickLabel(type: string, id: number) {
    this.advanceFilterService.removeFilter(type, id);
  }

  makeBlockWithContent(item: ActiveFilterData): Block {
    const block = this.itemContainer.clone();
    const itemNameBlock = block.children.find(
      ({settings: {viewPart}}) => viewPart === ViewPart.ITEM_NAME,
    );
    itemNameBlock.content = item.name;
    return block;
  }

  private initializeGroupContainerViewPart() {
    this.groupContainer = this.block.children.find(
      ({settings: {viewPart}}) => viewPart === ViewPart.GROUP_CONTAINER,
    );
    return this.groupContainer;
  }

  private initializeItemContainerViewPart() {
    this.itemContainer = this.groupContainer.children.find(
      ({settings: {viewPart}}) => viewPart === ViewPart.ITEM_CONTAINER,
    );
    return this.itemContainer;
  }
}
